export class ApiError extends Error {
  constructor(public status: number, message: string, public detail?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Bound each request, including its response body. Retry reads only. */
export async function reliableRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
  const readOnly = ['GET', 'HEAD'].includes((options.method ?? 'GET').toUpperCase());
  const timeoutMs = readOnly ? 20_000 : 120_000;
  for (let attempt = 0; ; attempt++) {
    const controller = new AbortController();
    const cancel = () => controller.abort(options.signal?.reason);
    options.signal?.addEventListener('abort', cancel, { once: true });
    if (options.signal?.aborted) cancel();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      if (!response.ok) {
        const raw = await response.text();
        let detail: unknown;
        try { detail = JSON.parse(raw); } catch { detail = raw; }
        const message = typeof detail === 'object' && detail !== null && 'detail' in detail
          ? String((detail as { detail: unknown }).detail)
          : `Request failed with status ${response.status}`;
        throw new ApiError(response.status, message, detail);
      }
      if (response.status === 204) return undefined as T;
      return await response.json() as T;
    } catch (error) {
      if (options.signal?.aborted) throw error;
      const temporary = error instanceof ApiError
        ? [408, 429, 502, 503, 504].includes(error.status)
        : error instanceof TypeError || controller.signal.aborted;
      if (!readOnly || !temporary || attempt >= 1) {
        if (controller.signal.aborted) {
          throw new Error('The server took too long to respond. Check your connection, then try again.');
        }
        throw error;
      }
    } finally {
      clearTimeout(timer);
      options.signal?.removeEventListener('abort', cancel);
    }
    await new Promise(resolve => setTimeout(resolve, 750));
  }
}
